/**
 * The `.anticon` rules `@ant-design/icons` used to inject at runtime, plus the
 * two extra rules this compat layer needs for `solid` / `badge` icons.
 *
 * We keep the `anticon` prefix on purpose: app stylesheets and tests target
 * `.anticon`, `.anticon-<name>` and `.anticon-spin` in ~30 places, so the DOM
 * contract has to survive the swap off the antd icon package.
 */
const STYLE_ID = 'wh-antd-compat-icons'

export const iconStyles = `
.anticon {
  display: inline-flex;
  align-items: center;
  color: inherit;
  font-style: normal;
  line-height: 0;
  text-align: center;
  text-transform: none;
  vertical-align: -0.125em;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.anticon > * {
  line-height: 1;
}

.anticon svg {
  display: inline-block;
}

.anticon::before {
  display: none;
}

.anticon[tabindex] {
  cursor: pointer;
}

.anticon-spin::before,
.anticon-spin {
  display: inline-block;
  -webkit-animation: loadingCircle 1s infinite linear;
  animation: loadingCircle 1s infinite linear;
}

/* Solid antd icons (StarFilled, EnvironmentFilled, …): fill the whole glyph. */
.anticon-solid > svg {
  fill: currentColor;
}

/*
 * Status-badge antd icons (CheckCircleFilled, WarningFilled, …): fill the outer
 * shape and knock the inner glyph out in the surface colour, the way the antd
 * two-layer filled icons read. Lucide always emits the outer shape first for
 * the icons mapped to this mode.
 *
 * --wh-icon-knockout is that surface colour. NOTHING in this repo sets it
 * today, so the knockout is always white. The visible consequence, toggle-on
 * only: a light-coloured badge glyph on a dark or tinted surface - a white
 * CheckCircleFilled / WarningFilled on a coloured button or chip - fills its
 * outer shape with currentColor (white) and strokes its inner detail the same
 * white, so it reads as a blob rather than as a tick or a bang. This is a
 * KNOWN GAP, not an oversight: it is the reference branch's own behaviour, and
 * the real answer (a surface-colour token every component sets) belongs with
 * the wider shadcn work when antd goes, not with an icon-backend swap.
 */
.anticon-badge > svg > :first-child {
  fill: currentColor;
}

.anticon-badge > svg > :not(:first-child) {
  stroke: var(--wh-icon-knockout, #fff);
}

/*
 * A few lucide inner details are painted with a "fill" presentation attribute
 * rather than a stroke (Tag's dot). Knock those out too — scoped to elements
 * that actually carry "fill", since filling the stroke-only siblings would
 * close and flood their open paths (checkmarks, chevrons).
 */
.anticon-badge > svg > :not(:first-child)[fill] {
  fill: var(--wh-icon-knockout, #fff);
}

@-webkit-keyframes loadingCircle {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes loadingCircle {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
`

// Every icon calls this from its render body, and a table page renders hundreds
// per commit — so the hot path is a boolean, not a DOM query. The
// `getElementById` check stays as the cold-start path (a second bundle copy of
// this module, or a sheet inserted by an earlier mount).
let inserted = false

export const insertIconStyles = () => {
  if (inserted) return
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) {
    inserted = true
    return
  }

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.innerHTML = iconStyles
  // Prepend so app-level overrides keep winning, as they did with antd's sheet.
  document.head.insertBefore(style, document.head.firstChild)
  inserted = true
}
